import React, {
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
  useState,
  useMemo,
} from "react";
import MobileDetect from "mobile-detect";
import { useScrollEvent } from "./common";

const parseItem = (item, imgProps = {}) => {
  if (typeof item === "string") {
    return <img src={item} alt="" {...imgProps} />;
  }
  if (typeof item === "function") {
    return item();
  }
  return item;
};

const toggleClassName = (
  c1,
  c2,
  isC1,
  classList = window.document.documentElement.classList
) => {
  if (isC1) {
    classList.remove(c2);
    classList.add(c1);
  } else {
    classList.remove(c1);
    classList.add(c2);
  }
};

export const useHtmlClassList = (className) => {
  useEffect(() => {
    if (className) {
      window.document.documentElement.classList.add(className);
    }

    return () => {
      if (className) {
        window.document.documentElement.classList.remove(className);
      }
    };
  }, [className]);
};

export const useBrowserLayout = ({
  designWidth = 1290,
  designHeight = 2796,
  designRatio = 1.24,
  appWidth = 800,
} = {}) => {
  const [scrolled, setScrolled] = useState(false);
  const [isAppLayout, setIsAppLayout] = useState(); // based width
  const [isMobileLayout, setIsMobileLayout] = useState(); // based ratio
  // based ua
  const [isMobile, setIsMobile] = useState();
  const [isNative, setIsNative] = useState();
  const [isWXBrowser, setIsWXBrowser] = useState(false);
  const [md, setMd] = useState();

  // 检查浏览器信息
  useEffect(() => {
    setIsNative(!!window.ReactNativeWebView);
    toggleClassName("is_native", "is_notNative", !!window.ReactNativeWebView);
    const md = new MobileDetect(window.navigator.userAgent);
    const mobile = !!md.mobile();
    setMd(md);
    setIsWXBrowser(
      /micromessenger/.test(window.navigator.userAgent.toLowerCase())
    );
    setIsMobile(mobile);

    toggleClassName("is_mobile", "is_desktop", mobile);
    if (mobile) {
      // ios safari 的导航条会挡住底部
      setTimeout(() => {
        const viewH = window.innerHeight; // 浏览器的视窗高度
        const pageH = window.document.documentElement.offsetHeight; // 页面高度
        if (pageH > viewH) {
          // 以便 css 那边做缩放，将导航条的部分流出来
          document.documentElement.style.setProperty(
            "--scale-for-safari",
            viewH / pageH
          );
        }
      }, 300);
    }
  }, []);

  useEffect(() => {
    const resizeEvent = () => {
      const winWidth = window.document.documentElement.clientWidth;
      const isApp = winWidth < appWidth;
      toggleClassName("is_app", "is_notApp", isApp);
      window.document.documentElement.classList.add("theme-disabled");
      setIsAppLayout(isApp);
    };
    resizeEvent();
    window.addEventListener("resize", resizeEvent);

    return () => {
      window.removeEventListener("resize", resizeEvent);
    };
  }, [appWidth]);

  // 检测网页是否近似手机比例
  useEffect(() => {
    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const matchRatio = w / h <= (designWidth * designRatio) / designHeight;
      setIsMobileLayout(matchRatio);
      // console.log(
      //   (w / h).toFixed(2),
      //   (designWidth / designHeight).toFixed(2),
      //   ((designWidth * designRatio) / designHeight).toFixed(2)
      // );
    };

    window.addEventListener("resize", resize);
    resize();

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, [designWidth, designHeight, designRatio]);

  // 为手机比例的网页添加 class
  useEffect(() => {
    toggleClassName("is_matchRatio", "is_notMatchRatio", isMobileLayout);
  }, [isMobileLayout]);

  // 当滑动后再加载后续内容
  // 避免一开始加载太多图片，且会阻塞首屏视频加载
  useScrollEvent(
    () => {
      setScrolled(true);
    },
    { firstTrigger: false }
  );

  const renderItem = useCallback(
    (
      forPc,
      forApp,
      { mode = "app", imgProps = {}, placeholder = null } = {}
    ) => {
      const isLayout = {
        app: isAppLayout,
        mobile: isMobileLayout,
        mobileDevice: isMobile,
      }[mode];

      if (isLayout === undefined) {
        return placeholder || null;
      }

      if (isLayout) {
        return parseItem(forApp, imgProps);
      }

      return parseItem(forPc, imgProps);
    },
    [isAppLayout, isMobileLayout, isMobile]
  );

  return {
    md,
    isReady: isAppLayout !== undefined,
    isMobile,
    isMobileLayout,
    isAppLayout,
    isWXBrowser,
    renderItem,
    isNative,
    scrolled,
  };
};

// 预加载图片
// prepareGroup 是一个二维数组，表示一组图片的集合
// finalTime 如果设置，则在这个时间后无论图片是否加载完成，都标记为 ready
// readyStatus 是一个数组，表示每组图片的加载完成情况
// ready 是一个布尔值，表示所有组的图片是否都加载完jjj
export const usePrepare = (
  prepareGroup,
  { finalTime = 8000, onPrepared } = {}
) => {
  const [ready, setReady] = useState(prepareGroup.map(() => false));
  useEffect(() => {
    // 准备好图片组的记载完成标记
    const allCheck = prepareGroup.map((x) => ({ isReady: false }));

    const prepare = (checkObj, imgs) => {
      let count = 0;
      const len = imgs.length;
      const counter = () => {
        count++;
        if (count === len) {
          checkObj.isReady = true;
        }
      };
      imgs.forEach((src) => {
        const img = new Image();
        img.onload = counter;
        img.src = src;
      });
    };

    prepareGroup.forEach((imgs, index) => {
      prepare(allCheck[index], imgs);
    });

    const check = setInterval(() => {
      const ready = allCheck.every((x) => x.isReady);
      setReady(allCheck.map((x) => x.isReady));
      if (ready) {
        onPrepared?.();
        clearInterval(check);
      }
    }, 300);

    // 无论是否加载完成，一定时间后后都展示页面
    if (finalTime) {
      setTimeout(() => {
        setReady(allCheck.map((x) => true));
        onPrepared?.();
        clearInterval(check);
      }, finalTime);
    }
  }, []);

  return {
    readyStatus: ready,
    ready: ready.every((x) => x),
  };
};

const calcFitFontSize = (container, title, _min, _max) => {
  if (!container) return;

  if (getComputedStyle(container).position === "static") {
    container.style.position = "relative";
  }

  // 为container创建一个child，用于测试
  const testBox = document.createElement("div");
  testBox.textContent = title;
  testBox.style.position = "absolute";
  testBox.style.top = "0";
  testBox.style.visibility = "hidden";
  testBox.style.whiteSpace = "nowrap";
  container.appendChild(testBox);

  let min = _min;
  let max = _max;
  let size = max;
  const containerWidth = container.offsetWidth;
  // 二分法查找最大合适字号
  while (min < max) {
    testBox.style.fontSize = size + "px";
    if (testBox.scrollWidth + 20 > container.offsetWidth) {
      max = size - 1;
    } else {
      min = size + 1;
    }
    size = Math.floor((min + max) / 2);
  }
  container.style.fontSize = size + "px";
  container.innerText = title; // 设置最终的标题内容
  testBox.remove();
};

export const useFitFontSize = (title, { minSize = 12, maxSize = 70 } = {}) => {
  const curContainerRef = useRef(null);
  const observerRef = useRef();

  const resizeObserver = useMemo(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (typeof ResizeObserver === "undefined") {
      return null;
    }

    observerRef.current = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === curContainerRef.current) {
          calcFitFontSize(curContainerRef.current, title, minSize, maxSize);
        }
      }
    });
    return observerRef.current;
  }, [title, minSize, maxSize]);

  const containerRef = useCallback(
    (dom) => {
      if (dom) {
        curContainerRef.current = dom;
        resizeObserver.observe(curContainerRef.current);
      } else if (curContainerRef.current) {
        resizeObserver.unobserve(curContainerRef.current);
        curContainerRef.current = null;
      }
    },
    [resizeObserver]
  );

  return {
    containerRef,
  };
};
